import {
    Delete as DeleteIcon,
    DoneAll as DoneIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Undo as UndoIcon,
} from '@mui/icons-material'
import { Box, Stack, Tooltip, IconButton, Typography } from '@mui/material'
import { useState } from 'react'

// Per-type accent color (drives the unread bar + the operator/value emphasis).
const TYPE_COLOR: Record<string, string> = {
    notifyUserChanges: '#8BC441',
    notifyBoatChanges: '#508991',
    notifyBoatSignal: '#E00000',
    notifyGeofencing: '#AA3E98',
}
const colorFor = (t: string) => TYPE_COLOR[t] || '#9AA0A6'

// Parse an alarm message like "Value: 10.97 Alarm ID: Analog_Input_1 <= 11":
//  - reading  → the live value text (used to exclude r / n readings)
//  - value    → the live value to one decimal (shown in parens), null if N/A
const parseAlarm = (message: string = '') => {
    const rm = message.match(/Value:\s*(.*?)(?:\s+Alarm ID|$)/i)
    const reading = (rm?.[1] || '').trim()
    const excluded =
        /^(r|n)$/i.test(reading) ||
        /\((r|n)\)/i.test(reading) ||
        /resolved/i.test(reading)
    const num = parseFloat(reading)
    const value = Number.isFinite(num) ? num.toFixed(1) : null
    return { reading, excluded, value }
}

// Exported so the parent can drop excluded notifications from its counts too.
export const isExcludedNotification = (n: any) =>
    parseAlarm(n?.userNotificationMessage).excluded

// Always a relative phrase ("just now" … "3y ago") — the absolute date/time is
// shown alongside it by formatStamp.
const relativeAgo = (d: any) => {
    const ts = new Date(d).getTime()
    if (!ts) return ''
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    if (s < 60) return 'just now'
    const m = Math.floor(s / 60)
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    const days = Math.floor(h / 24)
    if (days < 7) return `${days}d ago`
    const w = Math.floor(days / 7)
    if (w < 5) return `${w}w ago`
    const mo = Math.floor(days / 30)
    if (mo < 12) return `${mo}mo ago`
    return `${Math.floor(days / 365)}y ago`
}

// Timestamp line: "<relative> | <date>, <time>". The date and clock render in
// the timezone chosen in the fleet view (persisted to localStorage). 'boat' is
// per-boat / GPS-derived and not available here, so it falls back to the
// viewer's local zone — same as 'browser'.
const formatStamp = (d: any) => {
    const ts = new Date(d).getTime()
    if (!ts) return ''
    const mode =
        (typeof localStorage !== 'undefined' &&
            localStorage.getItem('osmosisFleetTz')) ||
        'browser'
    const zone = mode === 'utc' ? 'UTC' : undefined
    let date = ''
    let clock = ''
    try {
        date = new Date(ts).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(zone ? { timeZone: zone } : {}),
        })
        clock = new Date(ts).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            ...(zone ? { timeZone: zone } : {}),
            timeZoneName: 'short',
        })
    } catch {
        /* ignore formatting failures */
    }
    const abs = [date, clock].filter(Boolean).join(', ')
    const rel = relativeAgo(d)
    return abs ? `${rel} | ${abs}` : rel
}

const UserNotifications = ({
    userNotifications,
    handleDeleteNotification,
    handleMarkAsRead,
    emptyLabel = 'Nothing here',
}: any) => {
    const rows = (userNotifications || []).filter(
        (n: any) => !isExcludedNotification(n)
    )
    // Per-notification expand state → reveals the full userNotificationMessage.
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})
    const toggleExpanded = (id: string) =>
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

    if (rows.length === 0) {
        return (
            <Typography
                variant="body2"
                sx={{ color: 'text.disabled', px: 1, py: 2, fontSize: 12 }}
            >
                {emptyLabel}
            </Typography>
        )
    }

    return (
        <Stack spacing={0.5}>
            {rows.map((n: any) => {
                const {
                    _id,
                    userNotificationType,
                    userNotificationSubject,
                    userNotificationMessage,
                    userNotificationBoat: boat,
                    updatedAt,
                    read,
                } = n
                const accent = colorFor(userNotificationType)
                const { value } = parseAlarm(userNotificationMessage)

                const hin = boat?.fullHin || boat?.shortHin || ''
                const sub = [
                    [boat?.boatModel?.modelYear, boat?.boatModel?.model]
                        .filter(Boolean)
                        .join(' '),
                    boat?.dealer?.dealerName,
                ]
                    .filter(Boolean)
                    .join(' | ')
                // Description: strip the "<boat> Alarm:" prefix (boat context is
                // already shown above) → e.g. "Battery 1 Low".
                const description =
                    (userNotificationSubject || '').replace(
                        /^.*?\bAlarm:\s*/i,
                        ''
                    ) || userNotificationSubject
                const fullMessage = String(
                    userNotificationMessage || ''
                ).trim()
                const canExpand =
                    !!fullMessage &&
                    fullMessage.replace(/\s+/g, ' ') !== description
                const isOpen = !!expanded[_id]

                return (
                    <Box
                        key={_id}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            gap: 1,
                            p: 1.25,
                            pl: 1.75,
                            borderRadius: 1.5,
                            bgcolor: read
                                ? 'transparent'
                                : 'rgba(139,196,65,0.06)',
                            transition: 'background 0.2s ease',
                            '&:hover': {
                                bgcolor: 'action.hover',
                                '& .notif-actions': { opacity: 1 },
                            },
                            '&:before': read
                                ? {}
                                : {
                                      content: '""',
                                      position: 'absolute',
                                      left: 0,
                                      top: 8,
                                      bottom: 8,
                                      width: 3,
                                      borderRadius: 2,
                                      bgcolor: accent,
                                  },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* HIN (prominent) */}
                            {hin && (
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        lineHeight: 1.25,
                                        color: read
                                            ? 'text.secondary'
                                            : 'text.primary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {hin}
                                </Typography>
                            )}
                            {/* model year + model | dealer */}
                            {sub && (
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: 'text.secondary',
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {sub}
                                </Typography>
                            )}

                            {/* description (prominent) */}
                            <Typography
                                sx={{
                                    mt: hin ? 0.75 : 0,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: read
                                        ? 'text.secondary'
                                        : 'text.primary',
                                    lineHeight: 1.3,
                                }}
                            >
                                {description}
                                {value != null && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 0.5,
                                            fontWeight: 700,
                                            color: accent,
                                            opacity: read ? 0.7 : 1,
                                        }}
                                    >
                                        ({value})
                                    </Box>
                                )}
                            </Typography>
                            {canExpand && isOpen && (
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: 12.5,
                                        color: read
                                            ? 'text.secondary'
                                            : 'text.primary',
                                        lineHeight: 1.4,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {fullMessage}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: 'text.disabled',
                                    mt: 0.5,
                                }}
                            >
                                {formatStamp(updatedAt)}
                            </Typography>
                        </Box>

                        <Stack
                            className="notif-actions"
                            direction="row"
                            sx={{
                                flexShrink: 0,
                                // Keep icon buttons square so their round hover
                                // background doesn't stretch into an oval (Stack
                                // defaults to align-items: stretch).
                                alignItems: 'center',
                                alignSelf: 'flex-start',
                                opacity: { xs: 1, md: 0.35 },
                                transition: 'opacity 0.2s ease',
                            }}
                        >
                            {canExpand && (
                                <Tooltip
                                    title={isOpen ? 'Show less' : 'Show full message'}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => toggleExpanded(_id)}
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {isOpen ? (
                                            <ExpandLessIcon sx={{ fontSize: 17 }} />
                                        ) : (
                                            <ExpandMoreIcon sx={{ fontSize: 17 }} />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip
                                title={read ? 'Mark as unread' : 'Mark as read'}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => handleMarkAsRead(_id)}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    {read ? (
                                        <UndoIcon sx={{ fontSize: 17 }} />
                                    ) : (
                                        <DoneIcon sx={{ fontSize: 17 }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    onClick={() => handleDeleteNotification(_id)}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': { color: 'error.main' },
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                )
            })}
        </Stack>
    )
}

export default UserNotifications
